import React, { type ReactNode } from "react";
import { Text } from "@chakra-ui/react";
import { FillImage } from "components/images";
import { type WarehouseConfig } from "contexts/warehouse-cdp-context";
import firstCardImage from "../../../../public/assets/platform-overview-content/data-teams-card-1.png";
import secondCardImage from "../../../../public/assets/platform-overview-content/data-teams-card-2.png";
import thirdCardImage from "../../../../public/assets/platform-overview-content/data-teams-card-3.png";
import fourthCardImage from "../../../../public/assets/platform-overview-content/data-teams-card-4.png";
import fifthCardImage from "../../../../public/assets/platform-overview-content/data-teams-card-5.png";
import sixthCardImage from "../../../../public/assets/platform-overview-content/data-teams-card-6.png";
import { DynamicWarehouseCard3 } from "./card-3";

function StyledTitle({ children }: { children: ReactNode }) {
  return (
    <Text as="span" textStyle="body3">
      {children}
    </Text>
  );
}

function InlineDescription({ children }: { children: ReactNode }) {
  return (
    <Text as="span" fontWeight="normal" color="brand.textSecondary.light">
      {children}
    </Text>
  );
}

type StyledCardProps = {
  id: string;
  title: JSX.Element;
  media: JSX.Element;
};

export function getCards(warehouse?: WarehouseConfig): StyledCardProps[] {
  return [
    {
      id: "card-1",
      title: (
        <StyledTitle>
          Version control with Git{" "}
          <InlineDescription>
            allows you to manage pipelines using YAML files in a repo that syncs bidirectionally with Hightouch.
          </InlineDescription>
        </StyledTitle>
      ),
      media: (
        <FillImage
          src={firstCardImage}
          alt="Abstract illustration of Version control with Git showing YAML files for models and syncs automatically being synced both ways to and from the data warehouse with Hightouch."
        />
      ),
    },
    {
      id: "card-2",
      title: (
        <StyledTitle>
          Integrate directly with dbt{" "}
          <InlineDescription>
            to import your models, publish exposures back to dbt, and trigger syncs as soon as dbt jobs finish.
          </InlineDescription>
        </StyledTitle>
      ),
      media: (
        <FillImage
          src={secondCardImage}
          alt="Abstract illustration of models and exposures integrating between dbt and Hightouch."
        />
      ),
    },
    {
      id: "card-3",
      title: (
        <StyledTitle>
          Write logs back to {warehouse?.name ?? "the warehouse"}{" "}
          <InlineDescription>
            for auditing or analysis, giving you full control and unlimited retention.
          </InlineDescription>
        </StyledTitle>
      ),
      media: warehouse ? (
        <DynamicWarehouseCard3 warehouse={warehouse} />
      ) : (
        <FillImage
          src={thirdCardImage}
          alt="Abstract illustration of three different logs and metadata that are able to be shared with your data warehouse like Snowflake, Databricks, and more."
        />
      ),
    },
    {
      id: "card-4",
      title: (
        <StyledTitle>
          Step into the row-level debugger{" "}
          <InlineDescription>
            to trace every operation that occurs during a sync, including API calls for each processed row.
          </InlineDescription>
        </StyledTitle>
      ),
      media: (
        <FillImage
          src={fourthCardImage}
          alt="Abstract illustration of Hightouch UI displaying a detailed log in the debugger of a failed post."
        />
      ),
    },
    {
      id: "card-5",
      title: (
        <StyledTitle>
          Detect anomalies{" "}
          <InlineDescription>
            and proactively receive alerts on sync throughput, data quality, destination errors, and other problems.
          </InlineDescription>
        </StyledTitle>
      ),
      media: (
        <FillImage
          src={fifthCardImage}
          alt="Abstract illustration of a graph alerting to to a spike in invalid emails detected at 8:41AM on a Tuesday."
        />
      ),
    },
    {
      id: "card-6",
      title: (
        <StyledTitle>
          Build escalation flows{" "}
          <InlineDescription>
            with custom alerts that loop in stakeholders and help resolve data incidents more quickly.
          </InlineDescription>
        </StyledTitle>
      ),
      media: (
        <FillImage
          src={sixthCardImage}
          alt="Abstract UI displaying invalid credentials from a Salesforce account flowing into PagerDuty data engineering. Low throughput in Facebook Ads escalates to a marketing ops slack channel after 12 hours."
        />
      ),
    },
  ];
}
